import { Chart, Options, Point, SeriesOptions, SeriesOptionsType, SeriesPieDataLabelsOptionsObject, SeriesZonesOptionsObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import StoneXLogo from '../../../Components/Assets/SX_logo_chart.png';
import scssVariables from '../../../Config.module.scss';
import applicationSettings from '../../../Core/Settings/ApplicationSettings';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import { Currency, DataSeries, DemeterDataFrequency, MarketIndicatorOutlook, UnitOfMeasure } from '../../../Generated/Raven-Demeter';
import { indicatorColorByOutlookDefinitions } from '../../Pages/Administration/MarketIndicatorsManagement/MarketIndicatorsManagementDefinitions';
import downloadService from '../../Services/Download/DownloadService';
import formattingService from '../../Services/Formatting/FormattingService';
import languageService from '../../Services/Language/LanguageService';
import { translationKeys } from '../../Services/Language/TranslationDefinitions';
import {
    areaRangeChartType,
    ChartContext,
    ChartDisplayType,
    ChartOptionsDefinitions,
    defalutExportChartAxisTitleAndLabels,
    defaultChartOptions,
    defaultForecastLineWidth,
    defaultZoneAxis,
    featuredLineWidthForExport,
    HighchartsPlot,
    IChartAreaRangeData,
    IChartAreaRangeDataSeries,
    IChartBarDataSeries,
    IChartData,
    IChartDataSeries,
    IChartSeasonalDataSeries,
    IDownloadData,
    lineChartType,
    OptionsWithDownloads,
    shortDashStyle,
    singleLineWidthForExport,
} from './ChartDefinitions';
import { StudyToSeriesMap as studyToSeriesMap } from './Studies/StudiesDefinitions';

// Translations.
const forecastText = 'words.forecast';
const highText = 'words.high';
const lowText = 'words.low';

type TooltipOptions = {
    nameOverride?: string;
    format?: string;
    dataFrequency?: DemeterDataFrequency;
    displayDecimalPlacesMinimum?: number;
    displayDecimalPlacesMaximum?: number;
    customDateLabel?: string;
};
type DownloadType = 'jpeg' | 'svg' | 'pdf' | 'png' | 'csv' | 'xlsx';
type LinkedSeries = (SeriesOptions & { linkedTo: string })[];

// Platts and S&P Global licensing allows showing the trend but not individual point values.
// Matching 's&p global' rather than 's&p' keeps the unrestricted equity index tags (S&P 500, S&P Dow Jones Indices) out.
const restrictedDataSourcePattern = /platts|s&p global/i;

const maxDataInSeriesForDisplay = 3000;
const chartWidthMargin = 92.5;
const sourceLabelXPosition = 0;
const sourceLabelYPosition = 20;
const disclaimerLabelXPosition = 0;
const disclaimerLabelYPosition = 67;

/* eslint-disable class-methods-use-this */
class ChartService {
    // Defines base for a normal data series.
    getDataSeriesBase(series: IChartDataSeries | IChartAreaRangeDataSeries, type: string, color: string, ignoreLegendClick?: boolean) {
        const baseSeries = {
            name: series.label,
            type,
            data: [] as HighchartsPlot[] | number[],
            color,
            showInLegend: true,
            marker: {},
            events: {},
        };

        if (type === lineChartType) {
            baseSeries.marker = { enabled: false };
        }

        if (type === areaRangeChartType) {
            baseSeries.marker = { enabled: false };
        }

        if (ignoreLegendClick) {
            baseSeries.events = {
                legendItemClick() {
                    return false;
                },
            };
        }

        return baseSeries;
    }

    // Defines forecast base for a normal data series.
    getForecastDataSeriesBase(series: IChartDataSeries, color: string, handleForecastClicked: (chartContext: ChartContext) => void) {
        return {
            name: series.forecastLabel ?? `${series.label} ${languageService.translate(forecastText)}`,
            yAxis: 0,
            marker: {
                enabled: false,
            },
            events: {
                legendItemClick() {
                    handleForecastClicked(this as unknown as ChartContext);
                },
            },
            zoneAxis: defaultZoneAxis,
            color,
            type: lineChartType,
            dashStyle: shortDashStyle,
            lineWidth: defaultForecastLineWidth,
            showInLegend: true,
        };
    }

    // Returns a string 'currency / unitOfMeasure' for a chart axis.
    getCurrencyAndUnitOfMeasureText(unitOfMeasure?: UnitOfMeasure, currency?: Currency) {
        if (currency && unitOfMeasure && unitOfMeasure !== UnitOfMeasure.Index) {
            return `${currency} / ${languageService.translate(`unitOfMeasure.Short${unitOfMeasure}`)}`;
        }

        if (unitOfMeasure && unitOfMeasure !== UnitOfMeasure.Index) {
            return `${languageService.translate(`unitOfMeasure.Short${unitOfMeasure}`)}`;
        }

        if (currency) {
            return currency;
        }

        return '';
    }

    // Returns true when any of the chart's data sources may not expose point values on hover or in a data download.
    isRestrictedDataSource(...dataSourceTags: (string | null | undefined)[]) {
        return dataSourceTags.some((dataSourceTag) => !!dataSourceTag && restrictedDataSourcePattern.test(dataSourceTag));
    }

    // General tooltip formatted text for most chart types.
    getTooltipText(context: ChartContext, tooltipOptions?: TooltipOptions) {
        const { dataFrequency, format, nameOverride, displayDecimalPlacesMinimum, displayDecimalPlacesMaximum, customDateLabel } = tooltipOptions ?? {};
        const { x, y, point, series } = context as unknown as ChartContext;
        let { name } = series;

        const focusDate = point.originalAsOfDate ?? point.asOfDate ?? new Date(x);
        const isStudy = Object.values(studyToSeriesMap).some((baseStudy) => baseStudy.some((study) => study.type === series?.initialType));
        const year = formattingService.toYearFromUtc(focusDate);
        const monthYear =
            dataFrequency === DemeterDataFrequency.Daily || dataFrequency === DemeterDataFrequency.Weekly
                ? formattingService.toDayOfWeekWithDayMonthYear(focusDate)
                : formattingService.toMonthYear(focusDate);
        let formattedDate = customDateLabel ?? (dataFrequency === DemeterDataFrequency.Yearly ? year : monthYear);
        let formattedValue = formattingService.formatNumber(y, {
            minimumDecimalPlaces: displayDecimalPlacesMinimum,
            maximumDecimalPlaces: displayDecimalPlacesMaximum,
        });

        if (format === 'percent') {
            formattedValue = `${formattedValue}%`;
        }

        if (format === 'weekNumber') {
            formattedDate = `${languageService.translate(translationKeys.words.week)} ${point.index! + 1}`;
        }

        if (nameOverride && !point.zone?.value && !isStudy) {
            name = nameOverride;
        }

        if (context.point.lineDataType === 'range' || context.point.lineDataType === 'average') {
            formattedDate = formattingService.toMonth(focusDate.getMonth() + 1);
        }

        if (context.point.lineDataType === 'range') {
            formattedValue = `${formattingService.formatNumber(point.low ?? 0, {
                minimumDecimalPlaces: displayDecimalPlacesMinimum,
                maximumDecimalPlaces: displayDecimalPlacesMaximum,
            })} - ${formattingService.formatNumber(point.high ?? 0, {
                minimumDecimalPlaces: displayDecimalPlacesMinimum,
                maximumDecimalPlaces: displayDecimalPlacesMaximum,
            })}`;
        }

        return `<b>${formattedDate}</b><br /><span style="color: ${point.color}">\u25CF</span> ${name} : <b>${formattedValue}</b>`;
    }

    // Get a tooltip text with high point and low point.
    getAreaRangeTooltipText(context: ChartContext, tooltipOptions?: TooltipOptions) {
        const { x, point, series } = context;
        const { dataFrequency } = tooltipOptions ?? {};

        const monthYear =
            dataFrequency === DemeterDataFrequency.Daily || dataFrequency === DemeterDataFrequency.Weekly
                ? formattingService.toDayOfWeekWithDayMonthYear(new Date(x))
                : formattingService.toMonthYear(new Date(x));
        const formattedHighValue = formattingService.formatNumber(point.high ?? 0.0, {
            minimumDecimalPlaces: 0,
            maximumDecimalPlaces: applicationConstants.DefaultDisplayDecimalPlacesMaximum,
        });
        const formattedLowValue = formattingService.formatNumber(point.low ?? 0.0, {
            minimumDecimalPlaces: 0,
            maximumDecimalPlaces: applicationConstants.DefaultDisplayDecimalPlacesMaximum,
        });

        return `<b>${monthYear}</b><br /><span style="color: ${point.color}">\u25CF</span> ${series.name}<br/>
        ${languageService.translate(highText)}: <b>${formattedHighValue}</b><br/>
        ${languageService.translate(lowText)}: <b>${formattedLowValue}</b>`;
    }

    // Returns a singular download item in the proper format for a csv download.
    getDownloadItem(
        series: IChartBarDataSeries | IChartDataSeries,
        dataFrequency?: DemeterDataFrequency,
        onlyActualValues?: boolean,
        useForwardCurve?: boolean,
        displayDecimalPlacesMinimum?: number,
        displayDecimalPlacesMaximum?: number,
        rawDateFormatter?: (date: Date) => string,
    ) {
        const barChartSeries = series as IChartBarDataSeries;
        const dataSeries = useForwardCurve ? series.forwardCurveData : series.data;
        const downloadDataItem = dataSeries?.map((dataItem) => {
            let date = formattingService.toMonthYear(dataItem.asOfDate);

            if (dataFrequency === DemeterDataFrequency.Yearly) {
                date = `${dataItem.asOfDate.getFullYear()}`;
            }

            if (dataFrequency === DemeterDataFrequency.Weekly || dataFrequency === DemeterDataFrequency.Daily) {
                date = formattingService.toShortDayMonthYear(dataItem.asOfDate);
            }

            if (rawDateFormatter) {
                date = rawDateFormatter(dataItem.asOfDate);
            }

            return {
                value: formattingService.formatNumber(dataItem.value, {
                    minimumDecimalPlaces:
                        barChartSeries.displayDecimalPlacesMinimum ??
                        displayDecimalPlacesMinimum ??
                        formattingService.getDisplayDecimalPlacesMinimum(dataItem.value),
                    maximumDecimalPlaces:
                        barChartSeries.displayDecimalPlacesMaximum ??
                        displayDecimalPlacesMaximum ??
                        formattingService.getDisplayDecimalPlacesMinimum(dataItem.value),
                }),
                date,
                rawDate: formattingService.toApiDate(dataItem.asOfDate, true),
                isActualValue: onlyActualValues ? true : dataItem.isActualValue,
            };
        });

        return downloadDataItem;
    }

    // Returns a data in comma separated value format for download (special format for areaRange).
    getDownloadDataForAreaRange(
        allSeries: (IChartDataSeries | IChartAreaRangeDataSeries)[],
        dataSeries: ChartOptionsDefinitions[],
        numberOfDecimalPlaces?: number,
        onlyActualValues?: boolean,
    ) {
        const downloadData: IDownloadData[] = [];

        allSeries.forEach((series, index) => {
            const endIndex = series.data.length;

            (dataSeries[index].data as number[] | number[][]) = series.data.slice(0, endIndex).map((item) => {
                if ((item as IChartData).value) {
                    return [item.asOfDate.getTime(), (item as IChartData).value];
                }

                return [item.asOfDate.getTime(), (item as IChartAreaRangeData).minimumValue, (item as IChartAreaRangeData).maximumValue];
            });

            const downloadDataItem = this.getDownloadItem(
                series as IChartDataSeries,
                undefined,
                onlyActualValues,
                false,
                numberOfDecimalPlaces,
                numberOfDecimalPlaces,
            );

            downloadData.push({ label: series.label, data: downloadDataItem } as IDownloadData);
        });

        return downloadData;
    }

    // Returns a data in comma separated value format for download.
    getDownloadData(allSeries: IChartDataSeries[], dataFrequency?: DemeterDataFrequency, numberOfDecimalPlaces?: number) {
        const downloadData: IDownloadData[] = [];

        allSeries.forEach((series) => {
            const downloadSeries = { ...series };
            if (downloadSeries.allForecastDataIsForwardCurve) {
                downloadSeries.forwardCurveData = downloadSeries.data.filter((x) => !x.isActualValue);
                downloadSeries.data = downloadSeries.data.filter((x) => x.isActualValue);
            }

            const downloadDataItem = this.getDownloadItem(
                downloadSeries as IChartDataSeries,
                dataFrequency,
                false,
                false,
                numberOfDecimalPlaces,
                numberOfDecimalPlaces,
            );

            downloadData.push({ label: series.label, data: downloadDataItem } as IDownloadData);

            const forwardCurveDataItem = this.getDownloadItem(
                downloadSeries as IChartDataSeries,
                undefined,
                false,
                true,
                numberOfDecimalPlaces,
                numberOfDecimalPlaces,
            );

            if (forwardCurveDataItem && forwardCurveDataItem.length !== 0) {
                downloadData.push({ label: `${series.label} ${languageService.translate(translationKeys.text.forwardCurve)}`, data: forwardCurveDataItem });
            }
        });

        return downloadData;
    }

    getDownloadDataForSeasonalChart(allSeries: IChartSeasonalDataSeries[], dataFrequency?: DemeterDataFrequency, numberOfDecimalPlaces?: number) {
        const downloadData: IDownloadData[] = [];

        allSeries.forEach((series) => {
            const dataSeries = [
                {
                    ...series,
                },
            ];

            if (series.lineDataType === 'forecast') {
                dataSeries[0].data = series.data.filter((x) => !x.isActualValue);
            }

            if (series.lineDataType === 'range') {
                dataSeries[0].label = `${series.label} - ${languageService.translate(translationKeys.words.minimum)}`;
                dataSeries[0].data = series.data.map((dataItem) => ({
                    ...dataItem,
                    value: (dataItem as IChartAreaRangeData).minimumValue,
                }));

                dataSeries.push({
                    ...dataSeries[0],
                    label: `${series.label} - ${languageService.translate(translationKeys.words.maximum)}`,
                    data: series.data.map((dataItem) => ({
                        ...dataItem,
                        value: (dataItem as IChartAreaRangeData).maximumValue,
                    })),
                });
            }

            dataSeries.forEach((x) => {
                const downloadDataItem = this.getDownloadItem(
                    x as IChartDataSeries,
                    dataFrequency,
                    false,
                    false,
                    numberOfDecimalPlaces,
                    numberOfDecimalPlaces,
                    series.lineDataType === 'range' || series.lineDataType === 'average' ? (date) => formattingService.toMonth(date.getMonth()) : undefined,
                );

                downloadData.push({ label: x.label, data: downloadDataItem } as IDownloadData);
            });
        });

        return downloadData;
    }

    getOutlookColor = (outlook: MarketIndicatorOutlook, useWhiteAsNeutral?: boolean, useAlphaOpacity?: boolean) => {
        if (useWhiteAsNeutral && outlook === MarketIndicatorOutlook.Flat) {
            return scssVariables.plainWhite;
        }

        if (!useAlphaOpacity) {
            switch (outlook) {
                case MarketIndicatorOutlook.VeryBearish:
                    return scssVariables.veryBearishColor;
                case MarketIndicatorOutlook.Bearish:
                    return scssVariables.bearishColor;
                case MarketIndicatorOutlook.Flat:
                    return scssVariables.neutralColor;
                case MarketIndicatorOutlook.Bullish:
                    return scssVariables.bullishColor;
                case MarketIndicatorOutlook.VeryBullish:
                    return scssVariables.veryBullishColor;
                default:
                    return scssVariables.plainWhite;
            }
        }

        switch (outlook) {
            case MarketIndicatorOutlook.VeryBearish:
                return scssVariables.veryBearishColorAlpha;
            case MarketIndicatorOutlook.Bearish:
                return scssVariables.bearishColorAlpha;
            case MarketIndicatorOutlook.Flat:
                return scssVariables.neutralColor;
            case MarketIndicatorOutlook.Bullish:
                return scssVariables.bullishColorAlpha;
            case MarketIndicatorOutlook.VeryBullish:
                return scssVariables.veryBullishColorAlpha;
            default:
                return scssVariables.plainWhite;
        }
    };

    getChartExportForDownload = (
        name: string, // Internal name of the component
        type: DownloadType,
        isAdministrator: boolean,
        disclaimer: string,
        chartReference?: HighchartsReact.RefObject,
        title?: string,
        subtitle?: string,
        dataSourceTag?: string | string[],
        marketIndicatorOutlook?: MarketIndicatorOutlook,
    ) => {
        if (chartReference?.chart) {
            const { chart } = chartReference;
            const numberOfYAxes = chart.yAxis?.length || 2;
            const yAxisArray = Array(numberOfYAxes).fill(defalutExportChartAxisTitleAndLabels);
            const options: Options = {
                title: {
                    text: title,
                    align: 'left',
                    style: {
                        color: scssVariables.mainTextColor,
                        fontSize: '28px',
                        fontWeight: '700',
                    },
                },
                subtitle: {
                    text: subtitle,
                    align: 'left',
                    style: {
                        color: scssVariables.stonexMediumGray,
                        fontSize: '15px',
                        fontWeight: '500',
                    },
                },
                xAxis: defalutExportChartAxisTitleAndLabels,
                yAxis: yAxisArray,
                legend: {
                    itemStyle: {
                        fontSize: '12px',
                    },
                },
                chart: {
                    ...defaultChartOptions.chart,
                    spacingTop: 20,
                    spacingBottom: 70,
                    spacingLeft: 20,
                    spacingRight: 20,
                    events: {
                        load: (a) => {
                            const eventChart = a.target as unknown as Chart;

                            eventChart.renderer
                                .image(StoneXLogo, eventChart.chartWidth - 159, 0, 159, 66)
                                .addClass('credits')
                                .add();

                            const dataSource = Array.isArray(dataSourceTag)
                                ? dataSourceTag.filter((value, index, array) => array.indexOf(value) === index).join(', ')
                                : dataSourceTag ?? ' ';

                            const sourceLabel = eventChart.renderer
                                .label(dataSource, 0)
                                .css({ color: scssVariables.stonexMediumGray, fontSize: '9px', fontWeight: '400' })
                                .add();
                            sourceLabel.align(
                                {
                                    ...sourceLabel.getBBox(),
                                    align: 'center',
                                    x: sourceLabelXPosition,
                                    verticalAlign: 'bottom',
                                    y: sourceLabelYPosition,
                                },
                                undefined,
                                'spacingBox',
                            );

                            const disclaimerLabel = eventChart.renderer
                                .label(disclaimer, 0)
                                .css({
                                    color: scssVariables.stonexMediumGray,
                                    fontSize: '9px',
                                    fontWeight: '400',
                                    width: eventChart.chartWidth - chartWidthMargin,
                                })
                                .add();

                            disclaimerLabel.align(
                                {
                                    ...disclaimerLabel.getBBox(),
                                    align: 'center',
                                    x: disclaimerLabelXPosition,
                                    verticalAlign: 'bottom',
                                    y: disclaimerLabelYPosition,
                                },
                                undefined,
                                'spacingBox',
                            );

                            if (marketIndicatorOutlook) {
                                const exportMarketIndicatorTitleHtml = `
                                    <div style="
                                        color: ${
                                            indicatorColorByOutlookDefinitions.find((x) => x.marketIndicatorOutlook === marketIndicatorOutlook)?.textColor
                                        };
                                        font-size: 14px;
                                        font-weight: 600;
                                        height: 100%;
                                    ">
                                        ${languageService.translate(translationKeys.marketIndicatorOutlook[marketIndicatorOutlook])}
                                    </div> `;

                                const titleBBox = (eventChart.title?.element as SVGTextElement)?.getBBox();

                                eventChart.renderer
                                    .label(
                                        exportMarketIndicatorTitleHtml,
                                        titleBBox.x + titleBBox.width + 16,
                                        titleBBox.y,
                                        undefined,
                                        undefined,
                                        undefined,
                                        true,
                                    )
                                    .attr({
                                        zIndex: 7,
                                        padding: 8,
                                        fill: this.getOutlookColor(marketIndicatorOutlook),
                                        r: 4,
                                    })
                                    .add();
                            }
                        },
                    },
                },
                navigator: {
                    enabled: false,
                },
            };

            const currentNavigatorMax = chart.xAxis[0].max!;
            const currentNavigatorMin = chart.xAxis[0].min!;
            const numberOfIndicators = (chart.userOptions.series as LinkedSeries).filter((x) => x.linkedTo).length;

            const navigatorSeriesLength = (chart.userOptions.series as Options[])
                .filter((x) => x.data)
                .flatMap((x) => (x.data as Point[]).filter((y) => y?.x > currentNavigatorMin && y.x < currentNavigatorMax)).length;

            const totalNumberOfDataPoints = navigatorSeriesLength * (numberOfIndicators + 1);

            if (totalNumberOfDataPoints > maxDataInSeriesForDisplay) {
                options.plotOptions = {
                    series: {
                        dataGrouping: {
                            enabled: true,
                        },
                    },
                };
            }

            // Do this async so the dropdown closes.
            const defaultExportOptions = { filename: name, sourceWidth: 1280, sourceHeight: 720, url: applicationSettings.api!.ravenHighchartsUri };
            setTimeout(() => {
                const userOptions = chart?.userOptions as OptionsWithDownloads;
                const isDonutChart = userOptions?.chart?.type === ChartDisplayType.Pie.toLowerCase();
                const isAreaChart = userOptions?.chart?.type === areaRangeChartType;
                const isDonutSliceLabelsEnabled = (chart?.options?.plotOptions?.pie?.dataLabels as SeriesPieDataLabelsOptionsObject)?.enabled;

                const exportingSeries = chart?.userOptions?.series?.map((series, index: number) => {
                    const seriesExportOptions: SeriesOptionsType = { ...series };

                    if (
                        series.type === ChartDisplayType.Line.toLowerCase() ||
                        series.type === ChartDisplayType.Spline.toLowerCase() ||
                        series.type === undefined
                    ) {
                        const seriesWithPrimaryLine = series as SeriesOptions & { isPrimaryLine?: boolean };
                        const isPrimaryLine = (index === 0 && seriesWithPrimaryLine.isPrimaryLine !== false) || seriesWithPrimaryLine.isPrimaryLine;
                        const exportOptions = seriesExportOptions as SeriesOptions & { lineWidth?: number };
                        exportOptions.lineWidth = isPrimaryLine ? featuredLineWidthForExport : singleLineWidthForExport;
                        if (isPrimaryLine) {
                            seriesExportOptions.color = scssVariables.featuredLineColor;
                        }
                    }

                    if (series.type === ChartDisplayType.Column.toLowerCase()) {
                        const seriesWithZones = series as SeriesOptions & { zones?: SeriesZonesOptionsObject[]; data?: unknown[] };
                        if (seriesWithZones.zones) {
                            const dataLength = seriesWithZones.data?.length ?? 0;
                            type ZoneWithBorder = SeriesZonesOptionsObject & { borderWidth?: number };
                            const forecastZone = seriesWithZones.zones.find((zone: SeriesZonesOptionsObject) => (zone as ZoneWithBorder).borderWidth);
                            if (forecastZone) {
                                let exportBorderWidth = 3;
                                if (dataLength > 30) {
                                    exportBorderWidth = 3;
                                } else if (dataLength > 15) {
                                    exportBorderWidth = 2;
                                }

                                const exportOptionsWithZones = seriesExportOptions as SeriesOptions & {
                                    zones?: SeriesZonesOptionsObject[];
                                };
                                exportOptionsWithZones.zones = seriesWithZones.zones.map((zone: SeriesZonesOptionsObject) => {
                                    const zoneWithBorder = zone as ZoneWithBorder;
                                    if (zoneWithBorder.borderWidth) {
                                        return { ...zone, borderWidth: exportBorderWidth };
                                    }
                                    return zone;
                                });
                            }
                        }
                    }

                    return seriesExportOptions;
                });

                options.exporting = {
                    chartOptions: {
                        series: exportingSeries,
                    },
                };

                if (isAreaChart) {
                    chart?.userOptions?.series?.forEach((series) => {
                        series.showInLegend = true;
                    });
                    chart?.update({
                        series: chart?.userOptions?.series,
                    });
                }

                // Donut chart is unique in download. If we have a donut type, we will enable the labels for download
                // regardless of whether they are currently showing or not (same in popout mode). After
                // export is complete, we change it back.
                if (isDonutChart) {
                    chart?.update({
                        plotOptions: {
                            pie: {
                                dataLabels: {
                                    enabled: true,
                                    style: {
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        color: scssVariables.mainTextColor,
                                    },
                                },
                            },
                        },
                    });
                    if (chart?.userOptions?.subtitle?.text) {
                        const subtitleText = chart.userOptions.subtitle.text as string;
                        const textWithLineBreaks = subtitleText
                            .replace(/<p[^>]*>/gi, '')
                            .replace(/<\/p>/gi, '<br/>')
                            .replace(/<strong>/gi, '<b>')
                            .replace(/<\/strong>/gi, '</b>')
                            .trim()
                            .replace(/^<br\/>/, '')
                            .replace(/<br\/>$/, '');

                        options.subtitle = {
                            text: textWithLineBreaks,
                            align: 'center',
                            verticalAlign: 'middle',
                            useHTML: true,
                            floating: true,
                            y: 15,
                            style: {
                                color: scssVariables.mainTextColor,
                                fontSize: '32px',
                                fontWeight: '400',
                                textAlign: 'center',
                            },
                        };
                    }
                }

                switch (type) {
                    case 'jpeg':
                        chart.exportChart({ type: 'image/jpeg', ...defaultExportOptions }, options);
                        break;

                    case 'svg':
                        chart.exportChart({ type: 'image/svg+xml', ...defaultExportOptions }, options);
                        break;

                    case 'png':
                        chart.exportChart({ type: 'image/png', ...defaultExportOptions }, options);
                        break;

                    case 'pdf':
                        chart.exportChart({ type: 'application/pdf', ...defaultExportOptions }, options);
                        break;

                    case 'csv':
                        downloadService.downloadCsv(userOptions.downloadData as unknown as DataSeries[], name);
                        break;

                    case 'xlsx':
                        downloadService.downloadXlsx(userOptions.downloadData as unknown as DataSeries[], name);
                        break;

                    default:
                        break;
                }
                if (isDonutChart) {
                    chart?.update({ plotOptions: { pie: { dataLabels: { enabled: isDonutSliceLabelsEnabled } } } });
                }
                if (isAreaChart) {
                    chart?.userOptions?.series?.forEach((series) => {
                        if (series.type === 'arearange') {
                            series.showInLegend = false;
                        }
                    });
                    chart?.update({
                        series: chart.userOptions.series,
                    });
                }
            }, 0);
        }
    };
}

const chartService = new ChartService();

export default chartService;
